import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Optional,
  Self,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { NgControl, NgModel } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[sigvValidateIdNumber]',
})
export class ValidateIdNumberDirective implements OnInit, OnDestroy {
  @Input() keywords: string[] = [];
  /** 當前下拉選單的選取值 */
  @Input() selectKey: string = '';
  /** 要執行的驗證類型 */
  @Input() validateType: string = '';
  /** 輸出驗證結果：true 通過、false 不通過 */
  @Output() validationResult = new EventEmitter<boolean>();

  validateTypes = ['my-tin'];
  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl?: NgControl, // Reactive
    @Optional() private ngModel?: NgModel // Template-driven
  ) {}

  ngOnInit(): void {
    // Reactive form: 監聽 valueChanges
    if (this.ngControl?.valueChanges) {
      this.ngControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.doValidateDeferred());
    }

    // Template-driven form: 監聽 ngModel 的 valueChanges
    if (this.ngModel?.valueChanges) {
      this.ngModel.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.doValidateDeferred());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // input 輸入中就先試著驗
  @HostListener('input')
  onInput(): void {
    this.doValidateDeferred();
  }

  // 支援 updateOn:'blur' 與一般 blur 場景
  @HostListener('blur')
  onBlur(): void {
    this.doValidateDeferred();
  }

  ngOnChanges(): void {
    this.doValidateDeferred();
  }

  /** 等下一拍再驗，確保 ngModel/NgControl 已同步到最新值 */
  private doValidateDeferred(): void {
    Promise.resolve().then(() => this.doValidate());
  }

  private doValidate(): void {
    const raw = (this.currentValue() || '').trim().toUpperCase();

    // 條件不符合就不觸發驗證回傳
    if (
      !this.keywords.includes(this.selectKey) ||
      !this.validateTypes.includes(this.validateType)
    ) {
      return;
    }

    switch (this.validateType) {
      case 'my-tin':
        this.validationResult.emit(this.myTinvalidate(raw));
        break;
      default:
        break;
    }
  }

  /** 依序嘗試從 NgControl、NgModel、native input 取得值 */
  private currentValue(): string {
    if (this.ngControl && this.ngControl.value != null) {
      return this.ngControl.value as string;
    }
    if (this.ngModel && this.ngModel.model != null) {
      return this.ngModel.model as string;
    }
    return this.el.nativeElement.value;
  }

  // 與你既有規則一致
  private myTinvalidate(tin: string): boolean {
    if (!tin) return false;

    const igPrefix = 'IG';
    const validPrefixes = [
      'C',
      'CS',
      'D',
      'F',
      'FA',
      'PT',
      'TA',
      'TC',
      'TN',
      'TR',
      'TP',
      'J',
      'LE',
    ];

    const match = tin.match(/^([A-Z]+)(\d*)$/);
    if (!match) return false;

    const [, prefix, numberPart] = match;

    if (prefix === igPrefix) {
      return tin.length <= 14; // IG 可無數字，長度<=14
    }

    if (!validPrefixes.includes(prefix)) return false;
    if (!numberPart) return false;
    if (numberPart.length < 2) return false;
    if (numberPart[0] === '0') return false;
    if (numberPart[numberPart.length - 1] !== '0') return false;

    return true;
  }
}
